// Skript-Textbausteine: dieselbe deterministische [Token]-Ersetzung, die CallCockpit während des
// Calls anwendet — hierher gezogen, damit die Skript-Bibliothek (ScriptLibrary) exakt dieselbe
// Vorschau zeigen kann wie das, was der Setter später am Ohr liest. Keine Namen/Firmen aus der
// Vorlage sind hartkodiert — nur die Ersetzungslogik + der feste Anbieter-Name.
import type { Lead } from '../types';

// Eigener Firmenname — Vorgabe aus dem Leitkraft-Skript, kein Lead-Feld (jeder Call ist derselbe
// Absender). Änderungen hier wirken sofort auf jeden Block, der [Anbieter] verwendet.
export const ANBIETER_NAME = 'Leitkraft';

export interface ScriptTokenContext {
  vorname: string | null;
  nachname: string | null;
  jobtitel: string | null;
  // Abgeleitet aus dem Jobtitel (Rollenband → Abteilung), dieselbe Zuordnung wie das
  // Lead-Filter-Kriterium "Abteilung" (services.leadgen_people.department_for_title).
  abteilung?: string | null;
  unternehmen: string | null;
  setter: string | null;
  // Die Nische der Firma (`lead.niche`) — seit 2026-08-17 als [niche] im Skript verwendbar.
  niche?: string | null;
  paid: string | null;
  display: string | null;
  betrag: string | null;
  anbieter: string | null;
  // Rein manuelle Textbausteine ohne automatische Quelle — bleiben ohne Eingabe als sichtbarer
  // Sprech-Hinweis stehen (z. B. "[Anzeigentitel]"), genau wie jeder andere unbekannte Token.
  anzeigentitel?: string | null;
  kollege?: string | null;
  vorgesetzter?: string | null;
  vorgesetzterPosition?: string | null;
  domain?: string | null;
  // Nur gesetzt bei echtem Keyword-Beleg (§ 6 UWG) — server-seitiges Gate in
  // db_web/lead_gen.py::_attach_top_competitor, hier nur durchgereicht.
  wettbewerber?: string | null;
  wettbewerberGrund?: string | null;
}

export function firstName(full?: string | null): string {
  return (full || '').trim().split(/\s+/)[0] || '';
}

export function lastName(full?: string | null): string {
  const p = (full || '').trim().split(/\s+/);
  return p.length > 1 ? p.slice(1).join(' ') : '';
}

export function pct(x?: number | null): string | null {
  return x == null ? null : String(Math.round(x * 100));
}

// Rückholbaren Betrag konservativ ABRUNDEN auf eine runde Zahl (der Angerufene soll eine greifbare
// Hausnummer hören, keine krumme Zahl): KMU → Tausender, größer → 5er-/10er-Tausender.
export function roundDownEur(eur: number): string {
  const step = eur >= 100000 ? 10000 : eur >= 10000 ? 5000 : 1000;
  const r = Math.max(step, Math.floor(eur / step) * step);
  return r.toLocaleString('de-DE');
}

// Der Bausteinkasten — EINE Liste, aus der beides entsteht: die Ersetzung im Call und das, was der
// Editor anbietet, wenn jemand `[` tippt. Bis 2026-08-17 waren das zwei Listen (die Ersetzungs-Map
// hier, ein `TOKENS`-Array in ScriptLibrary.tsx). Wer einen Baustein ergänzte, ergänzte ihn in der
// Regel nur an einer Stelle — dann stand er entweder im Skript und wurde nie gefüllt, oder er
// füllte und niemand wusste, dass es ihn gibt.
//
// `key` ist der Name, wie er im Skript steht: `[name]`. Geschrieben wird er klein — so tippt man
// im Gespräch, und die Auflösung ist ohnehin schreibungsblind (siehe unten). `aliase` hält die
// früheren, deutschen Namen am Leben: in den Skripten auf Prod steht `[Vorname]`, und ein Baustein,
// der nach einer Umbenennung stumm wird, ist schlimmer als ein unschöner Name.
export interface ScriptTokenDef {
  key: string;
  label: string;          // wie er in der Auswahlliste heisst
  hinweis: string;        // woher der Wert kommt — die Frage, die man beim Schreiben hat
  aliase?: string[];
  wert: (ctx: ScriptTokenContext) => string | null | undefined;
}

export const SCRIPT_TOKENS: ScriptTokenDef[] = [
  { key: 'name', label: 'Vorname', hinweis: 'Vorname des Leads',
    aliase: ['Vorname'], wert: (c) => c.vorname },
  { key: 'nachname', label: 'Nachname', hinweis: 'Nachname des Leads',
    aliase: ['Nachname'], wert: (c) => c.nachname },
  { key: 'firmenname', label: 'Firmenname', hinweis: 'Name der Firma',
    aliase: ['Unternehmen'], wert: (c) => c.unternehmen },
  { key: 'job', label: 'Job', hinweis: 'Funktion der Person, z. B. Head of Marketing',
    aliase: ['Jobtitel'], wert: (c) => c.jobtitel },
  { key: 'abteilung', label: 'Abteilung', hinweis: 'Abgeleitet aus dem Jobtitel, z. B. Performance Teams',
    wert: (c) => c.abteilung },
  { key: 'niche', label: 'Nische', hinweis: 'Nische der Firma',
    aliase: ['Nische'], wert: (c) => c.niche },
  { key: 'callername', label: 'Anrufer', hinweis: 'Dein Vorname (der des Anrufers)',
    aliase: ['Setter', 'Mitarbeitervorname'], wert: (c) => c.setter },
  { key: 'anbieter', label: 'Anbieter', hinweis: `Der eigene Firmenname (${ANBIETER_NAME})`,
    wert: (c) => c.anbieter },
  { key: 'domain', label: 'Domain', hinweis: 'Domain der Firma, z. B. für die Mailadresse',
    aliase: ['Domain'], wert: (c) => c.domain },
  { key: 'betrag', label: 'Betrag', hinweis: 'Geschätzt rückholbarer Betrag pro Jahr',
    wert: (c) => c.betrag },
  { key: 'paid', label: 'paid %', hinweis: 'Anteil Paid-Traffic', wert: (c) => c.paid },
  { key: 'display', label: 'display %', hinweis: 'Anteil Display-Traffic', wert: (c) => c.display },
  // Ohne automatische Quelle: bleiben im Call sichtbar stehen und sind damit ein Sprech-Hinweis
  // („hier den Anzeigentitel nennen"), kein Fehler.
  { key: 'anzeigentitel', label: 'Anzeigentitel', hinweis: 'Von Hand — bleibt sonst sichtbar stehen',
    aliase: ['Anzeigentitel'], wert: (c) => c.anzeigentitel },
  { key: 'kollege', label: 'Kollege', hinweis: 'Von Hand — bleibt sonst sichtbar stehen',
    aliase: ['Kollege'], wert: (c) => c.kollege },
  { key: 'vorgesetzter', label: 'Vorgesetzter', hinweis: 'Von Hand — bleibt sonst sichtbar stehen',
    aliase: ['Vorgesetzter'], wert: (c) => c.vorgesetzter },
  { key: 'vorgesetzter position', label: 'Vorgesetzter-Pos.', hinweis: 'Von Hand — bleibt sonst sichtbar stehen',
    aliase: ['Vorgesetzter Position'], wert: (c) => c.vorgesetzterPosition },
  { key: 'wettbewerber', label: 'Wettbewerber', hinweis: 'Nur bei echtem Keyword-Beleg — sonst sichtbar stehen',
    wert: (c) => c.wettbewerber },
  { key: 'wettbewerber grund', label: 'Wettbewerber-Grund', hinweis: 'Nur bei echtem Keyword-Beleg — sonst sichtbar stehen',
    wert: (c) => c.wettbewerberGrund },
];

// Ersetzt jeden [Baustein] aus SCRIPT_TOKENS, seine früheren Namen und `[Vorname Nachname]`.
// Nachgeschlagen wird OHNE Rücksicht auf Gross-/Kleinschreibung: wer im Skript `[Name]` schreibt,
// meint `[name]`, und ein Token, der wegen eines grossen Buchstabens stumm bleibt, fällt erst im
// Gespräch auf. Unbekannter oder leerer Wert → der Token bleibt sichtbar (Cue für den Setter,
// hier von Hand etwas einzusetzen).
export function fillPlaceholders(text: string, ctx: ScriptTokenContext): string {
  const map = new Map<string, string | null | undefined>();
  const setze = (name: string, v: string | null | undefined) => map.set(name.toLowerCase(), v);
  for (const tok of SCRIPT_TOKENS) {
    setze(tok.key, tok.wert(ctx));
    for (const alias of tok.aliase ?? []) setze(alias, tok.wert(ctx));
  }
  setze('Vorname Nachname', `${ctx.vorname || ''} ${ctx.nachname || ''}`.trim() || null);
  return (text || '').replace(/\[([^\]]+)\]/g, (m, key) => {
    const v = map.get(String(key).trim().toLowerCase());
    return v != null && v !== '' ? v : m;
  });
}

// Baut den Token-Kontext aus Lead + Absender — dieselbe Ableitung, die CallCockpit für den echten
// Call verwendet. `setterName` ist typischerweise der ausgewählte LinkedIn-Account-Name oder,
// als Rückfall, der eingeloggte Nutzername.
// `person` ist die tatsächlich angerufene Person, falls sie vom Lead abweicht: der Sales-CRM-Lead
// gehört der FIRMA (eine Firma, ein Lead), die zweite und dritte Person eines Hauses erben sonst
// den Namen der ersten — und das Skript begrüßt den Falschen. Fehlt sie (Anruf aus der Lead-Liste,
// Zentrale, Vorschau), bleibt alles beim Lead.
export function buildScriptTokenContext(
  lead: Lead | null | undefined,
  setterName?: string | null,
  person?: { name?: string | null; position?: string | null } | null,
): ScriptTokenContext {
  const sc = lead?.script_context ?? undefined;
  const personName = (person?.name || '').trim() || null;
  return {
    vorname: firstName(personName ?? lead?.name),
    nachname: lastName(personName ?? lead?.name),
    jobtitel: (person?.position || '').trim() || sc?.job_title || null,
    abteilung: sc?.department ?? null,
    unternehmen: lead?.company || sc?.company || null,
    // Die Nische steht am Lead und wird vor dem Call ohnehin abgefragt (Soft Gate in der
    // Pre-Call-Karte) — sie ist damit zum Zeitpunkt des Anrufs bekannt.
    niche: (lead?.niche || '').trim() || null,
    paid: pct(sc?.paid_share),
    display: pct(sc?.display_share),
    setter: firstName(setterName),
    anbieter: ANBIETER_NAME,
    domain: sc?.domain ?? null,
    betrag: sc?.recoverable_eur != null && sc.recoverable_eur > 0
      ? `~${roundDownEur(sc.recoverable_eur)} €/Jahr`
      : 'einen vier- bis fünfstelligen Betrag/Jahr',
    wettbewerber: sc?.wettbewerber ?? null,
    wettbewerberGrund: sc?.wettbewerber_grund ?? null,
  };
}

// Beispielhafter Vorschau-Kontext für die Skript-Bibliothek — kein echter Lead vorhanden, also
// sprechende Platzhalter statt leerer Werte, damit die Vorschau zeigt, WIE ein befüllter Block
// aussieht (bracket-Tokens ohne Beispielwert bleiben bewusst sichtbar, siehe fillPlaceholders).
export function previewScriptTokenContext(): ScriptTokenContext {
  return {
    vorname: 'Max', nachname: 'Mustermann', jobtitel: 'Head of Marketing', abteilung: 'Performance Teams',
    unternehmen: 'Muster GmbH',
    setter: 'Alex', anbieter: ANBIETER_NAME, paid: '62', display: '18', niche: 'E-Commerce',
    betrag: '~24.000 €/Jahr', domain: 'muster-gmbh.de',
    wettbewerber: 'wettbewerber-gmbh.de', wettbewerberGrund: 'gemeinsamer Term',
  };
}
